import React from 'react';
import { Link } from 'react-router-dom';
import { Heart, MessageSquare, Share2, Play, Pause, MoreVertical, Trash2 } from 'lucide-react';
import { cn } from '../../lib/utils';
import { useAuth } from '../../contexts/AuthContext';
import { toggleLike } from '../../lib/posts';

interface PostProps {
  id: string;
  author: {
    id: string;
    username: string;
    image?: string;
    badge?: string;
  };
  content: string;
  audio?: {
    url: string;
    title: string;
    duration: string;
  };
  image?: string;
  timestamp: string;
  likes: number;
  comments: number;
  onDelete?: (id: string) => void;
}

export function Post({ id, author, content, audio, image, timestamp, likes, comments, onDelete }: PostProps) {
  const [isPlaying, setIsPlaying] = React.useState(false);
  const [isLiked, setIsLiked] = React.useState(false);
  const [likesCount, setLikesCount] = React.useState(likes);
  const [showMenu, setShowMenu] = React.useState(false);
  const { user } = useAuth();
  const menuRef = React.useRef<HTMLDivElement>(null);

  // Close menu when clicking outside
  React.useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLike = async () => {
    if (!user) return;

    try {
      const liked = await toggleLike(id);
      setIsLiked(liked);
      setLikesCount(prev => liked ? prev + 1 : prev - 1);
    } catch (error) {
      console.error('Error toggling like:', error);
    }
  };

  const isOwnPost = user?.id === author.id;

  return (
    <div className="p-4 border-b border-divider">
      <div className="flex gap-4">
        <Link to={`/u/${author.username}`}>
          <img
            src={author.avatar_url || `https://api.dicebear.com/7.x/initials/svg?seed=${author.username}`}
            alt={author.username}
            className="w-10 h-10 rounded-full"
          />
        </Link>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <Link 
              to={`/u/${author.username}`}
              className="font-medium hover:text-accent-primary"
            >
              {author.username}
            </Link>
            {author.badge && (
              <span className="px-2 py-0.5 bg-background-secondary rounded-full text-xs text-text-secondary">
                {author.badge}
              </span>
            )}
            <span className="text-text-tertiary">•</span>
            <span className="text-text-tertiary text-sm">{timestamp}</span>

            {isOwnPost && (
              <div className="relative ml-auto" ref={menuRef}>
                <button
                  onClick={() => setShowMenu(!showMenu)}
                  className="p-1 text-text-secondary hover:text-text-primary rounded-full"
                >
                  <MoreVertical size={16} />
                </button>

                {showMenu && (
                  <div className="absolute right-0 mt-1 w-32 bg-background-secondary border border-divider rounded-md shadow-lg z-10">
                    <button
                      onClick={() => {
                        onDelete?.(id);
                        setShowMenu(false);
                      }}
                      className="w-full px-3 py-2 text-left text-sm text-red-500 hover:bg-background-tertiary flex items-center gap-2"
                    >
                      <Trash2 size={14} />
                      Löschen
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
          
          <p className="mt-2 text-text-secondary">{content}</p>

          {audio && (
            <div className="mt-3 p-3 bg-background-secondary rounded-lg">
              <div className="flex items-center gap-4">
                <button
                  onClick={() => setIsPlaying(!isPlaying)}
                  className="w-10 h-10 flex items-center justify-center bg-accent-primary rounded-full hover:bg-accent-hover"
                >
                  {isPlaying ? <Pause size={18} /> : <Play size={18} />}
                </button>
                <div className="flex-1">
                  <div className="h-1 bg-background-tertiary rounded-full">
                    <div className="h-full w-1/3 bg-accent-primary rounded-full" />
                  </div>
                  <div className="flex justify-between mt-1">
                    <span className="text-xs text-text-tertiary">{audio.title}</span>
                    <span className="text-xs text-text-tertiary">{audio.duration}</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {image && (
            <img
              src={image}
              alt="Post content"
              className="mt-3 rounded-lg w-full max-h-96 object-cover"
            />
          )}

          <div className="flex items-center gap-6 mt-4">
            <button
              onClick={handleLike}
              className={cn(
                "flex items-center gap-2 text-text-secondary hover:text-accent-primary",
                isLiked && "text-accent-primary"
              )}
            >
              <Heart size={18} fill={isLiked ? "currentColor" : "none"} />
              <span>{likesCount}</span>
            </button>
            <button className="flex items-center gap-2 text-text-secondary hover:text-accent-primary">
              <MessageSquare size={18} />
              <span>{comments}</span>
            </button>
            <button className="flex items-center gap-2 text-text-secondary hover:text-accent-primary">
              <Share2 size={18} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}